import React, { useState, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import { toast } from 'react-toastify';
import EditLoglineModal from './EditLoglineModal';

const SelectedScriptsList = ({ onOpenModal }) => {
  const { scripts, urls } = usePage().props;
  const [loadingScriptHash, setLoadingScriptHash] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState(null);
  
  // Modal state for editing loglines
  const [editLoglineModalOpen, setEditLoglineModalOpen] = useState(false);
  const [editingScript, setEditingScript] = useState(null);

  // Filter and create local state for reordering
  const filteredScripts = scripts ? scripts.filter(script => 
    script.has_profile && script.is_public
  ) : [];

  const [localScripts, setLocalScripts] = useState(filteredScripts);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local scripts when props change
  useEffect(() => {
    const newScripts = scripts ? scripts.filter(script => 
      script.has_profile && script.is_public
    ) : [];
    
    if (JSON.stringify(newScripts.map(s => s.hash)) !== JSON.stringify(localScripts.map(s => s.hash))) {
      setLocalScripts(newScripts);
    }
  }, [scripts]);

  const openEditLoglineModal = (script) => {
    // Pass the complete script data including current logline
    const scriptWithCurrentLogline = {
      ...script,
      currentLogline: script.profile_settings?.logline || script.logline || script.loglines?.[0] || ''
    };
    
    setEditingScript(scriptWithCurrentLogline);
    setEditLoglineModalOpen(true);
  };

  const closeEditLoglineModal = () => {
    setEditLoglineModalOpen(false);
    setEditingScript(null);
    // No complex state management - the page refresh in the modal handles updates
  };

  const startEditingNote = (script) => {
    setEditingNote(script.hash);
    setNoteText(script.featured_note || '');
  };

  const saveNote = async (scriptHash) => {
    setLoadingScriptHash(scriptHash);
    
    try {
      const response = await fetch(`${urls.scriptUpdate}${scriptHash}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          featured_note: noteText.trim(),
          is_public: true,
          allow_comments: true,
          allow_ratings: true, 
          allow_connection_requests: true,
          is_featured: true,
        }),
      });

      if (response.ok) {
        toast.success('Script note updated.');
        setEditingNote(null);
        setNoteText('');
        
        // Update local state for notes (this one works fine)
        setLocalScripts(prev => prev.map(s => 
          s.hash === scriptHash 
            ? { 
                ...s, 
                featured_note: noteText.trim(), 
                profile_settings: { 
                  ...s.profile_settings, 
                  featured_note: noteText.trim() 
                } 
              }
            : s
        ));
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      toast.error('Failed to update script note.');
    } finally {
      setLoadingScriptHash(null);
    }
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setNoteText('');
  };

  const removeScript = async (scriptHash) => {
    setLoadingScriptHash(scriptHash);
    try {
      const response = await fetch(`${urls.scriptUpdate}${scriptHash}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ is_public: false }),
      });

      if (response.ok) {
        toast.success('Script removed from profile.');
        setLocalScripts(prev => prev.filter(script => script.hash !== scriptHash));
        setTimeout(() => {
          router.reload({ only: ['scripts'] });
        }, 500);
      } else {
        throw new Error('Failed to remove script');
      }
    } catch (err) {
      toast.error('Failed to remove script.');
    } finally {
      setLoadingScriptHash(null);
    }
  };

  const updateDisclosureLevel = async (script, level) => {
    setLoadingScriptHash(script.hash);
    
    try {
      const response = await fetch(`${urls.scriptUpdate}${script.hash}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          disclosure_level: level,
          is_public: true,
          allow_comments: script.profile_settings?.allow_comments ?? true,
          allow_ratings: script.profile_settings?.allow_ratings ?? true,
          allow_connection_requests: script.profile_settings?.allow_connection_requests ?? true,
          is_featured: script.profile_settings?.is_featured ?? true,
        }),
      });

      if (response.ok) {
        toast.success('Disclosure level updated.');
        setLocalScripts(prev => prev.map(s => 
          s.hash === script.hash 
            ? { ...s, profile_settings: { ...s.profile_settings, disclosure_level: level } }
            : s
        ));
        setTimeout(() => {
          router.reload({ only: ['scripts'] });
        }, 1000);
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      toast.error('Failed to update disclosure level.');
    } finally {
      setLoadingScriptHash(null);
    }
  };

  // Drag and drop handlers (keeping existing functionality)
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    setDropIndicatorIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropIndicatorIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropIndicatorIndex(null);
      return;
    }

    let insertIndex = dropIndex;
    if (draggedIndex < dropIndex) {
      insertIndex = dropIndex - 1;
    }

    const newOrder = [...localScripts];
    const draggedItem = newOrder[draggedIndex];
    
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(insertIndex, 0, draggedItem);

    setLocalScripts(newOrder);
    setHasChanges(true);
    
    setDraggedIndex(null);
    setDropIndicatorIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropIndicatorIndex(null);
  };

  const saveOrder = async () => {
    if (!hasChanges) return;

    const scriptOrder = localScripts.map(script => script.hash);

    try {
      const response = await fetch(urls.scriptReorder, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          script_order: scriptOrder
        }),
      });

      if (response.ok) {
        toast.success('Script order saved successfully.');
        setHasChanges(false);
        setTimeout(() => {
          router.reload({ only: ['scripts'] });
        }, 500);
      } else {
        throw new Error('Failed to save order');
      }
    } catch (err) {
      toast.error('Failed to save script order.');
    }
  };

  const DropIndicator = ({ index }) => {
    if (dropIndicatorIndex !== index || draggedIndex === null) return null;
    
    return (
      <tr>
        <td colSpan="6" className="p-0">
          <div className="w-100 bg-primary" style={{ height: '3px', opacity: 0.8, borderRadius: '1px' }}></div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fa fa-film me-2"></i>
            Scripts on Your Profile
            {hasChanges && <span className="badge bg-warning text-dark ms-2">Unsaved Order</span>}
          </h5>
          <button 
            type="button" 
            className="btn btn-primary btn-sm"
            onClick={onOpenModal}
          >
            <i className="fa fa-plus me-1"></i>
            Add Scripts
          </button>
        </div>
        
        <div className="card-body">
          {localScripts.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th width="40"></th>
                    <th>Title & Logline</th>
                    <th width="200">Additional Info</th>
                    <th width="160">Disclosure Level</th>
                    <th width="120">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <DropIndicator index={0} />
                  
                  {localScripts.map((script, index) => (
                    <React.Fragment key={script.hash}>
                      <tr 
                        draggable={editingNote !== script.hash}
                        onDragStart={(e) => editingNote !== script.hash && handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`${draggedIndex === index ? 'opacity-50 bg-light' : ''} ${loadingScriptHash === script.hash ? 'table-secondary' : ''}`}
                        style={{ 
                          cursor: draggedIndex === index ? 'grabbing' : 'default',
                          userSelect: 'none'
                        }}
                      >
                        <td className="text-center align-middle">
                          <div className="text-muted drag-handle" style={{ cursor: editingNote === script.hash ? 'default' : 'grab' }}>
                            <i className="fa fa-grip-vertical"></i>
                          </div>
                        </td>
                        
                        <td className="align-middle">
                          <div className="fw-semibold mb-1">{script.title}</div>
                          <div className="small text-muted mb-2">
                            {(() => {
                              // Get the current logline from multiple possible sources
                              const currentLogline = script.profile_settings?.logline || 
                                                    script.logline || 
                                                    script.loglines?.[0] || '';
                              
                              return currentLogline ? (
                                <div className="d-flex align-items-start gap-2">
                                  <div className="flex-grow-1">
                                    <span className="text-dark">
                                      {currentLogline.length > 100 
                                        ? currentLogline.substring(0, 100) + '...' 
                                        : currentLogline}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => openEditLoglineModal(script)}
                                    className="btn btn-sm btn-outline-secondary"
                                    title="Edit logline"
                                    style={{fontSize: '0.75rem', padding: '2px 6px'}}
                                  >
                                    <i className="fa fa-edit"></i>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => openEditLoglineModal(script)}
                                  className="btn btn-sm btn-outline-primary"
                                  style={{fontSize: '0.75rem'}}
                                >
                                  <i className="fa fa-plus me-1"></i>
                                  Add logline
                                </button>
                              );
                            })()}
                          </div>
                          <small className="text-muted">{script.time_created}</small>
                        </td>
                        
                        <td className="align-middle">
                          {editingNote === script.hash ? (
                            <div className="d-flex gap-1">
                              <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Add a note about this script..."
                                className="form-control form-control-sm"
                                rows="2"
                                style={{fontSize: '0.85rem'}}
                                autoFocus
                              />
                              <div className="d-flex flex-column gap-1">
                                <button
                                  onClick={() => saveNote(script.hash)}
                                  className="btn btn-sm btn-success"
                                  disabled={loadingScriptHash === script.hash}
                                  title="Save note"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="btn btn-sm btn-secondary"
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div 
                              onClick={() => startEditingNote(script)}
                              className="p-2 rounded border-dashed"
                              style={{
                                cursor: 'pointer',
                                minHeight: '40px',
                                border: '1px dashed #dee2e6',
                                backgroundColor: script.featured_note ? 'transparent' : '#f8f9fa',
                                transition: 'background-color 0.2s ease'
                              }}
                              title="Click to edit note"
                            >
                              {script.featured_note ? (
                                <small className="text-dark">
                                  {script.featured_note.length > 80 
                                    ? script.featured_note.substring(0, 80) + '...' 
                                    : script.featured_note}
                                </small>
                              ) : (
                                <small className="text-muted fst-italic">
                                  Anything you wish to tell people about this script?
                                </small>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="align-middle">
                          <select 
                            className="form-select form-select-sm"
                            disabled={loadingScriptHash === script.hash}
                            value={script.profile_settings?.disclosure_level || 'partial'}
                            onChange={(e) => updateDisclosureLevel(script, e.target.value)}
                          >
                            <option value="full">Full script and analysis available</option>
                            <option value="partial">Overview only</option>
                          </select>
                        </td>
                        
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            {loadingScriptHash === script.hash ? (
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <button 
                                type="button" 
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removeScript(script.hash)}
                                title="Remove from profile"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      <tr 
                        className="drop-zone"
                        style={{ height: '8px', background: 'transparent' }}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, index + 1)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index + 1)}
                      >
                        <td colSpan="6" style={{ padding: '4px 0', height: '8px' }}>
                          <DropIndicator index={index + 1} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              
              {localScripts.length > 1 && (
                <div className="mt-2">
                  <small className="text-muted">
                    <i className="fa fa-grip-vertical me-1"></i>
                    Drag and drop rows to reorder your scripts
                  </small>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="fa fa-film fa-3x mb-3 text-secondary"></i>
              <h5>No scripts selected for your profile</h5>
              <p className="text-muted">
                Choose which scripts to display on your public profile.
              </p>
              <button 
                type="button" 
                className="btn btn-primary mt-2"
                onClick={onOpenModal}
              >
                <i className="fa fa-plus me-1"></i>
                Select Scripts
              </button>
            </div>
          )}
        </div>
        
        {localScripts.length > 0 && hasChanges && (
          <div className="card-footer bg-warning bg-opacity-10 border-warning">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-warning-emphasis">
                <i className="fa fa-exclamation-triangle me-1"></i>
                You have unsaved changes to script order
              </small>
              <button 
                type="button"
                className="btn btn-warning btn-sm"
                onClick={saveOrder}
              >
                <i className="fa fa-save me-1"></i>
                Save Order
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Logline Modal */}
      <EditLoglineModal
        isOpen={editLoglineModalOpen}
        onClose={closeEditLoglineModal}
        script={editingScript}
        updateUrl={urls.scriptUpdate}
      />
    </>
  );
};

export default SelectedScriptsList;
